import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { useMemo, useState } from 'react';

import { type CampaignActivityDataType, useTopCampaignActivityByVendorIds } from '@/api/campaign';
import { type ActivityDataType, type TRAFFIC_TYPES } from '@/api/common';
import { type VendorActivityDataType, type VendorAndChannelType } from '@/api/vendor';
import {
  InstantSearchDataTable,
  type InstantSearchDataTableColumnDef,
} from '@/components/InstantSearch';
import { usePageFilterContext } from '@/components/page';
import { Panel } from '@/components/panels';
import type SolAPIError from '@/error/SolAPIError';
import DataTableAdvancedFiltersDialog from '@/features/AdvancedFiltersModal/DataTableAdvancedFiltersDialog';
import { DataTableMetricSelector } from '@/features/DataTableMetricSelector';
import { VisitActivityLegend } from '@/features/VisitActivityLegend';

import VendorActivityTableActionMenu from './VendorActivityTableActionMenu';

type VendorActivityTableRowType = 'vendor' | 'campaign' | 'campaignSummary' | 'campaignLoading';

export type VendorActivityTableRow = ActivityDataType & {
  id: string;
  rowType: VendorActivityTableRowType;
  vendor: VendorAndChannelType;
  name: string;
  campaignId?: string;
  campaignLabelRowSpan?: number;
  showCampaignLabel?: boolean;
  subRows?: VendorActivityTableRow[];
};

const EMPTY_ACTIVITY_METRICS: ActivityDataType = {
  adClicks: 0,
  adImpressions: 0,
  allVisits: 0,
  allVisitsBest: false,
  allVisitsWorst: false,
  baseAccounts: 0,
  botVisits: 0,
  closedWonFirstTouchAttribution: 0,
  closedWonInfluenced: 0,
  closedWonLinearAttribution: 0,
  companiesReached: 0,
  countCampaigns: 0,
  countClosedwonInfluenced: 0,
  countPipelineInfluenced: 0,
  engagedCompanies: 0,
  engagedCompaniesBest: false,
  engagedCompaniesWorst: false,
  fitScore: 0,
  fitScoreBest: false,
  fitScoreWorst: false,
  leadsInfluenced: 0,
  opportunitiesInfluenced: 0,
  opportunityInfluencedBest: false,
  opportunityInfluencedWorst: false,
  otherVisits: 0,
  percentOfEngagedCompanies: 0,
  percentOpportunitiesInfluenced: 0,
  pipelineFirstTouchAttribution: 0,
  pipelineInfluenced: 0,
  pipelineInfluencedBest: false,
  pipelineInfluencedPerSpend: 0,
  pipelineInfluencedWorst: false,
  pipelineLastTouchAttribution: 0,
  pipelineLinearAttribution: 0,
  spend: 0,
  spendPerTargetVisit: 0,
  spendPerVisitBest: false,
  spendPerVisitWorst: false,
  targetVisits: 0,
  targetViewThroughVisits: 0,
  unresolvedVisits: 0,
  unresolvedViewThroughVisits: 0,
  viewThroughPercent: 0,
  viewThroughVisits: 0,
  visitEfficiency: 0,
  otherViewThroughVisits: 0,
  botViewThroughVisits: 0,
};

const toCampaignRow = (campaignActivity: CampaignActivityDataType): VendorActivityTableRow => ({
  ...campaignActivity,
  id: campaignActivity.ad.id,
  rowType: 'campaign',
  vendor: campaignActivity.ad.vendor,
  name: campaignActivity.ad.name,
  campaignId: campaignActivity.ad.id,
});

const createCampaignSummaryRow = (vendor: VendorAndChannelType): VendorActivityTableRow => ({
  ...EMPTY_ACTIVITY_METRICS,
  id: `summary-${vendor.id}`,
  rowType: 'campaignSummary',
  vendor,
  name: t`All ${vendor.name} Campaigns`,
});

const createCampaignLoadingRows = (
  vendor: VendorAndChannelType,
  rowCount: number,
): VendorActivityTableRow[] =>
  Array.from({ length: rowCount }, (_, index) => ({
    ...EMPTY_ACTIVITY_METRICS,
    id: `loading-${vendor.id}-${index}`,
    rowType: 'campaignLoading',
    vendor,
    name: t`Loading Campaigns...`,
  }));

const withCampaignLabelRowSpan = (subRows: VendorActivityTableRow[]): VendorActivityTableRow[] => {
  if (subRows.length === 0) {
    return subRows;
  }

  return subRows.map((row, index) =>
    index === 0
      ? {
          ...row,
          showCampaignLabel: true,
          campaignLabelRowSpan: subRows.length,
        }
      : row,
  );
};

const toVendorRow = (
  vendorActivity: VendorActivityDataType,
  subRows: VendorActivityTableRow[] | undefined,
): VendorActivityTableRow => ({
  ...vendorActivity,
  id: vendorActivity.vendor.id,
  rowType: 'vendor',
  vendor: vendorActivity.vendor,
  name: vendorActivity.vendor.name,
  subRows,
});

type Props = {
  data?: VendorActivityDataType[];
  columns: InstantSearchDataTableColumnDef<VendorActivityTableRow>[];
  totalResults?: number;
  isLoading?: boolean;
  error: SolAPIError | null;
  onDisabledSeriesChange: (disabledSeriesType?: TRAFFIC_TYPES[]) => void;
};

const VendorActivityTable = ({
  data,
  columns,
  totalResults,
  isLoading,
  error,
  onDisabledSeriesChange,
}: Props) => {
  const {
    pageFilters: { audience, startDate, endDate },
  } = usePageFilterContext();

  const [metricSelectorOpen, setMetricSelectorOpen] = useState(false);
  const [advancedFiltersOpen, setAdvancedFiltersOpen] = useState(false);
  const [expandedRowIds, setExpandedRowIds] = useState<string[]>([]);

  const expandedVendorIds = useMemo(() => {
    const vendorIds = new Set((data ?? []).map((vendorActivity) => vendorActivity.vendor.id));
    return expandedRowIds.filter((vendorId) => vendorIds.has(vendorId));
  }, [data, expandedRowIds]);

  const { campaignActivityByVendorId, loadingVendorIds } = useTopCampaignActivityByVendorIds({
    vendorIds: expandedVendorIds,
    startDate,
    endDate,
    audienceId: audience?.id,
    limit: 5,
  });

  const tableRows = useMemo(() => {
    const loadingVendorIdSet = new Set(loadingVendorIds);

    return (data ?? []).map((vendorActivity) => {
      const vendorId = vendorActivity.vendor.id;
      const topCampaignActivity = campaignActivityByVendorId[vendorId];
      const subRows = (() => {
        if (topCampaignActivity != null) {
          const campaignRows = topCampaignActivity.map(toCampaignRow);
          const summaryRow = createCampaignSummaryRow(vendorActivity.vendor);
          return withCampaignLabelRowSpan([...campaignRows, summaryRow]);
        }

        if (loadingVendorIdSet.has(vendorId)) {
          return withCampaignLabelRowSpan(createCampaignLoadingRows(vendorActivity.vendor, 3));
        }

        return undefined;
      })();

      return toVendorRow(vendorActivity, subRows);
    });
  }, [campaignActivityByVendorId, data, loadingVendorIds]);

  return (
    <Panel
      title={<Trans>Visits By Vendor</Trans>}
      actions={
        <VendorActivityTableActionMenu
          onOpenMetricSelector={() => setMetricSelectorOpen(true)}
          onOpenAdvancedFilters={() => setAdvancedFiltersOpen(true)}
        />
      }
      verifyC99Tag
      isFullHeight
    >
      <VisitActivityLegend audience={audience} onDisabledSeriesChange={onDisabledSeriesChange} />
      <div data-testid="vendors-table">
        <InstantSearchDataTable<VendorActivityTableRow>
          columns={columns}
          data={tableRows}
          totalResults={totalResults}
          loading={isLoading}
          error={error}
          emptyMessage={<Trans>No vendors found.</Trans>}
          pagination={false}
          getRowId={(record) => record.id}
          initialColumnPinning={{
            left: ['__expand', 'channel.id', 'vendorId'],
          }}
          expandable={{
            variant: 'subRows',
            mode: 'accordion',
            expandedRowIds,
            onExpandedRowIdsChange: setExpandedRowIds,
            expandColumnWidth: 32,
            durationMs: 300,
            getSubRows: (row) => row.subRows,
            getRowCanExpand: (row) =>
              row.original.rowType === 'vendor' && row.original.countCampaigns > 0,
          }}
        />
      </div>
      <DataTableMetricSelector
        open={metricSelectorOpen}
        onClose={() => setMetricSelectorOpen(false)}
      />
      <DataTableAdvancedFiltersDialog
        open={advancedFiltersOpen}
        onClose={() => setAdvancedFiltersOpen(false)}
      />
    </Panel>
  );
};

export default VendorActivityTable;
